import { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { apiFetch, getBackendUrl } from "../../../api";
import s from "./ManageLessonsPanel.module.css";

const BACKEND = getBackendUrl();

/* ─── Confirm Dialog ─── */

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className={s.dialogBackdrop} onClick={onCancel}>
      <div className={s.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={s.dialogIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
        </div>
        <p className={s.dialogMessage}>{message}</p>
        <div className={s.dialogActions}>
          <button className={s.dialogCancel} onClick={onCancel}>Cancel</button>
          <button className={s.dialogConfirm} onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Tiny helpers ─── */

function GripIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" opacity="0.35">
      <circle cx="9" cy="5" r="1.5" />
      <circle cx="15" cy="5" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="19" r="1.5" />
      <circle cx="15" cy="19" r="1.5" />
    </svg>
  );
}

function StoryMiniCard({ story, action, actionLabel, isDragging, dragHandleProps, onOpenInWorkshop, onDelete, validationError, isValidating }) {
  return (
    <div className={`${s.miniCard} ${isDragging ? s.miniCardDragging : ""} ${validationError ? s.miniCardError : ""}`}>
      <div className={s.miniCardRow}>
        {dragHandleProps && (
          <span className={s.grip} {...dragHandleProps}>
            <GripIcon />
          </span>
        )}
        <div className={s.miniAvatar}>
          {story.has_avatar ? (
            <img
              className={s.miniAvatarImg}
              src={`${BACKEND}/api/v1/admin/story-avatar/${story.story_id}`}
              alt=""
            />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.4">
              <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
            </svg>
          )}
        </div>
        <div className={s.miniContent}>
          <span className={s.miniTitle}>{story.topic_in_swedish}</span>
          <span className={s.miniDesc}>{story.topic_in_english}</span>
        </div>
        {(onOpenInWorkshop || onDelete) && (
          <div className={s.miniCenter}>
            {onOpenInWorkshop && (
              <button className={s.workshopBtn} onClick={() => onOpenInWorkshop(story.story_id)} title="Open in Workshop">
                <span className={s.workshopBtnLabel}>Workshop</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </button>
            )}
            {onDelete && (
              <button className={s.deleteBtn} onClick={() => onDelete(story.story_id)} title="Delete story">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </button>
            )}
          </div>
        )}
        {isValidating ? (
          <span className={s.miniActionChecking}>
            <div className={s.miniSpinner} />
            <span className={s.miniActionLabel}>Checking</span>
          </span>
        ) : action && (
          <button className={s.miniAction} onClick={action} title={actionLabel}>
            <span className={s.miniActionLabel}>{actionLabel}</span>
            {actionLabel === "Add" ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 5v14m-7-7h14" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        )}
      </div>
      {validationError && validationError.length > 0 && (
        <div className={s.validationError}>
          <svg className={s.validationIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
          <div className={s.validationMessages}>
            {validationError.map((msg, i) => (
              <span key={i} className={s.validationMsg}>{msg}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SortableStoryCard({ story, onRemove, onOpenInWorkshop, validationError }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: story.story_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <StoryMiniCard
        story={story}
        action={() => onRemove(story.story_id)}
        actionLabel="Remove"
        isDragging={isDragging}
        dragHandleProps={listeners}
        onOpenInWorkshop={onOpenInWorkshop}
        validationError={validationError}
      />
    </div>
  );
}

/* ─── Main panel ─── */

export default function ManageLessonsPanel({ onOpenInWorkshop, onDeleteStory, onCreateNewStory }) {
  const [available, setAvailable] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(true);

  const [board, setBoard] = useState([]);
  const [loadingBoard, setLoadingBoard] = useState(true);

  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [validationErrors, setValidationErrors] = useState({});
  const [validatingId, setValidatingId] = useState(null);

  const [activeId, setActiveId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  /* ── Fetch available stories ── */
  const fetchAvailable = useCallback(async () => {
    setLoadingAvailable(true);
    try {
      const res = await apiFetch("/api/v1/admin/manage-lessons/available-stories");
      if (!res.ok) throw new Error("Failed to load stories");
      const data = await res.json();
      setAvailable(data.stories);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAvailable(false);
    }
  }, []);

  /* ── Fetch board stories ── */
  const fetchBoard = useCallback(async () => {
    setLoadingBoard(true);
    try {
      const res = await apiFetch("/api/v1/admin/manage-lessons/board-stories");
      if (!res.ok) throw new Error("Failed to load board");
      const data = await res.json();
      setBoard(data.stories);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingBoard(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailable();
    fetchBoard();
  }, [fetchAvailable, fetchBoard]);

  /* ── Add to board (with validation) ── */
  const handleAdd = useCallback(async (storyId) => {
    const story = available.find((s) => s.story_id === storyId);
    if (!story) return;

    setValidationErrors((prev) => { const n = { ...prev }; delete n[storyId]; return n; });
    setValidatingId(storyId);

    try {
      const res = await apiFetch(`/api/v1/admin/manage-lessons/validate-story/${storyId}`);
      if (!res.ok) {
        setValidationErrors((prev) => ({ ...prev, [storyId]: ["Validation check failed"] }));
        return;
      }
      const data = await res.json();
      if (!data.is_complete) {
        setValidationErrors((prev) => ({ ...prev, [storyId]: data.missing }));
        return;
      }
    } catch {
      setValidationErrors((prev) => ({ ...prev, [storyId]: ["Could not reach server"] }));
      return;
    } finally {
      setValidatingId(null);
    }

    setBoard((prev) => [...prev, story]);
    setAvailable((prev) => prev.filter((s) => s.story_id !== storyId));
    setDirty(true);
    setSaveSuccess(false);
  }, [available]);

  /* ── Remove from board ── */
  const handleRemove = useCallback((storyId) => {
    const story = board.find((s) => s.story_id === storyId);
    if (!story) return;
    setBoard((prev) => prev.filter((s) => s.story_id !== storyId));
    setAvailable((prev) => [story, ...prev]);
    setValidationErrors((prev) => { const n = { ...prev }; delete n[storyId]; return n; });
    setDirty(true);
    setSaveSuccess(false);
  }, [board]);

  /* ── Delete story ── */
  const handleDeleteConfirmed = useCallback(async () => {
    if (!confirmDeleteId) return;
    try {
      await onDeleteStory(confirmDeleteId);
      setAvailable((prev) => prev.filter((s) => s.story_id !== confirmDeleteId));
      setBoard((prev) => prev.filter((s) => s.story_id !== confirmDeleteId));
    } catch {
      // error handled by parent
    }
    setConfirmDeleteId(null);
  }, [confirmDeleteId, onDeleteStory]);

  /* ── Reorder via drag ── */
  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setBoard((prev) => {
      const oldIndex = prev.findIndex((s) => s.story_id === active.id);
      const newIndex = prev.findIndex((s) => s.story_id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      setDirty(true);
      setSaveSuccess(false);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  /* ── Submit changes ── */
  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setError("");
    setSaveSuccess(false);
    try {
      const res = await apiFetch("/api/v1/admin/manage-lessons/board-stories", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ story_ids: board.map((s) => s.story_id) }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (data?.detail?.incomplete_stories) {
          const msg = data.detail.message || "Some stories are incomplete";
          setError(msg);
          const errs = {};
          for (const item of data.detail.incomplete_stories) {
            errs[item.story_id] = item.missing;
          }
          setValidationErrors((prev) => ({ ...prev, ...errs }));
        } else {
          throw new Error(typeof data?.detail === "string" ? data.detail : "Failed to save");
        }
        return;
      }
      setDirty(false);
      setSaveSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [board]);

  const activeStory = activeId ? board.find((s) => s.story_id === activeId) : null;

  return (
    <div className={s.panel}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerTopRow}>
          <div className={s.headerIcon}>
            <svg className={s.headerIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          </div>
          <div className={s.headerText}>
            <h2 className={s.headerTitle}>Manage Lessons</h2>
            <p className={s.headerSubtitle}>Add stories to the board and arrange their order</p>
          </div>
          <div className={s.headerActions}>
            {onCreateNewStory && (
              <button className={s.newStoryBtn} onClick={onCreateNewStory}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 5v14m-7-7h14" />
                </svg>
                New Story
              </button>
            )}
            {dirty && (
              <button
                className={s.submitBtn}
                onClick={handleSubmit}
                disabled={saving || validatingId !== null}
              >
                {saving ? "Saving..." : "Submit Changes"}
              </button>
            )}
            {saveSuccess && !dirty && (
              <span className={s.savedBadge}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </span>
            )}
          </div>
        </div>
        {error && <p className={s.errorMsg}>{error}</p>}
      </div>

      {/* Two-column body */}
      <div className={s.body}>
        {/* LEFT — Available stories */}
        <div className={s.column}>
          <div className={s.colHeader}>
            <h3 className={s.colTitle}>All Stories</h3>
            <span className={s.colCount}>{available.length}</span>
          </div>
          <div className={s.colContent}>
            {loadingAvailable ? (
              <div className={s.colEmpty}>
                <div className={s.spinner} />
                <span>Loading stories...</span>
              </div>
            ) : available.length === 0 ? (
              <div className={s.colEmpty}>
                <span>All stories are on the board</span>
              </div>
            ) : (
              available.map((story) => (
                <StoryMiniCard
                  key={story.story_id}
                  story={story}
                  action={validatingId ? null : () => handleAdd(story.story_id)}
                  actionLabel="Add"
                  isValidating={validatingId === story.story_id}
                  validationError={validationErrors[story.story_id] ?? null}
                  onOpenInWorkshop={onOpenInWorkshop}
                  onDelete={setConfirmDeleteId}
                />
              ))
            )}
          </div>
        </div>

        {/* RIGHT — Board stories (sortable) */}
        <div className={s.column}>
          <div className={s.colHeader}>
            <h3 className={s.colTitle}>Board Lessons</h3>
            <span className={s.colCount}>{board.length}</span>
          </div>
          <div className={s.colContent}>
            {loadingBoard ? (
              <div className={s.colEmpty}>
                <div className={s.spinner} />
                <span>Loading board...</span>
              </div>
            ) : board.length === 0 ? (
              <div className={s.colEmpty}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.25">
                  <path d="M12 5v14m-7-7h14" />
                </svg>
                <span>Add stories from the left panel</span>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={board.map((s) => s.story_id)}
                  strategy={verticalListSortingStrategy}
                >
                  {board.map((story, i) => (
                    <div key={story.story_id} className={s.sortableRow}>
                      <span className={s.rowIndex}>{i + 1}</span>
                      <SortableStoryCard story={story} onRemove={handleRemove} onOpenInWorkshop={onOpenInWorkshop} validationError={validationErrors[story.story_id] ?? null} />
                    </div>
                  ))}
                </SortableContext>
                <DragOverlay>
                  {activeStory ? (
                    <StoryMiniCard story={activeStory} isDragging />
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </div>
      </div>

      {confirmDeleteId && (
        <ConfirmDialog
          message="Are you sure you want to delete this story? This action cannot be undone."
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
}
